import { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { __ } from "services/Translator";
import moneyFormatter from "services/moneyFormatter";
import { VIEW_META } from "./data";
import {
  CashierSharedContext,
  useCashierData,
  useVipDataSource,
  useBonusDataSource,
  useWithdrawableAmount,
} from "./useCashierData";
import Sidebar from "./Sidebar";
import DepositView from "./DepositView";
import WithdrawView from "./WithdrawView";
import BonusesView from "./BonusesView";
import VipView from "./VipView";
import HistoryView from "./HistoryView";
import LimitsView from "./LimitsView";
import { CloseIcon } from "./icons";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Top-level GCC cashier redesign modal + shell. Faithful port of the delivered
// Hub, wired to live cashier/VIP/bonus data. This component is a NEW, standalone
// surface and does not touch the existing CashierModals cashier.
export default function CashierRedesign({ open = true, onClose, initialTab = "deposit" }) {
  const [view, setView] = useState(initialTab === "withdraw" ? "withdraw" : "deposit");
  // Claim handoff: Bonuses "Claim" preselects this campaign in the Deposit view
  // client-side (no server persistence — the submit sends the id explicitly).
  const [claimedBonusId, setClaimedBonusId] = useState(null);
  const cashier = useCashierData();
  // Fetch VIP + bonuses ONCE here and share them via context so the views
  // (VipView/BonusesView) consume the same data without a second request.
  const vipData = useVipDataSource();
  const bonusData = useBonusDataSource();
  // Engine withdrawable (single source, shared with the Withdraw card).
  const { withdrawable: engineWithdrawable } = useWithdrawableAmount();
  const vip = vipData.vip;
  const campaigns = bonusData.campaigns;
  const shared = useMemo(
    () => ({ vip: vipData, bonus: bonusData }),
    // Memoize on the underlying data, not the per-render wrapper objects, so the
    // context value is stable and consumers only re-render when data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vipData.vip, vipData.available, vipData.loading, bonusData.active, bonusData.campaigns, bonusData.loading]
  );
  // Single source of truth: the same redux userBalance the app header reads, so
  // the cashier BALANCE (total = cash + bonus) always matches the header.
  const userBalance = useSelector((state) => state.userBalance);

  const modalRef = useRef(null);
  const openerRef = useRef(null);

  // Keep the latest onClose in a ref so the focus-trap effect below does NOT
  // depend on onClose's identity. Callers pass a fresh inline onClose on every
  // render (e.g. FullLayout's 10s balance poll), and if that were a dependency
  // the trap would re-run and call first.focus() — stealing focus out of the
  // amount input mid-typing (and dismissing the mobile keyboard). See the amount
  // "can't type" bug.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Accessible modal: trap focus while open, close on Escape, restore focus to
  // the opener on close. Runs only on the open->true transition.
  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement;
    const node = modalRef.current;
    if (!node) return undefined;

    const focusables = () =>
      Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => el.offsetParent !== null);

    const first = focusables()[0];
    if (first) first.focus();
    else node.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (typeof onCloseRef.current === "function") onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const els = focusables();
      if (!els.length) return;
      const firstEl = els[0];
      const lastEl = els[els.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    node.addEventListener("keydown", onKeyDown);
    return () => {
      node.removeEventListener("keydown", onKeyDown);
      const opener = openerRef.current;
      if (opener && typeof opener.focus === "function") opener.focus();
    };
    // Intentionally depends only on `open` — onClose is read via onCloseRef so a
    // new onClose identity per parent render can't re-run this and steal focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) setView(initialTab === "withdraw" ? "withdraw" : "deposit");
  }, [open, initialTab]);

  if (!open) return null;

  const [title, sub] = VIEW_META[view];
  const handleClose = typeof onClose === "function" ? onClose : () => {};

  // BALANCE = TOTAL (cash + bonus) from redux userBalance, formatted with the
  // wallet currency (falls back to the cashier currency). userBalance
  // null/loading -> null, and the UI shows a skeleton rather than a fake number.
  const ubCurrency =
    (userBalance && userBalance.wallets && userBalance.wallets[0] && userBalance.wallets[0].currency) ||
    cashier.currency;
  const totalNum = userBalance
    ? userBalance.total_balance != null && userBalance.total_balance !== ""
      ? parseFloat(userBalance.total_balance)
      : parseFloat(userBalance.cash_balance || 0) + parseFloat(userBalance.bonus_balance || 0)
    : NaN;
  const balanceFormatted = Number.isFinite(totalNum) ? moneyFormatter.balanceFormat(totalNum, ubCurrency) : null;
  // Withdrawable prefers the engine's total_withdrawal_amount (the exact figure
  // the Withdraw card shows) so the sidebar and the card never diverge; it falls
  // back to redux cash_balance only until that engine read resolves.
  const cashNum = userBalance ? parseFloat(userBalance.cash_balance) : NaN;
  const withdrawNum = engineWithdrawable != null ? engineWithdrawable : cashNum;
  const withdrawable = Number.isFinite(withdrawNum) ? moneyFormatter.balanceFormat(withdrawNum, ubCurrency) : null;
  const bonusesCount = Array.isArray(campaigns) ? campaigns.length : 0;

  return (
    <CashierSharedContext.Provider value={shared}>
    <div className="gcc-cashier">
      <div className="lobby">
        <div className="tiles">
          {Array.from({ length: 24 }).map((_, i) => (
            <i key={i} />
          ))}
        </div>
      </div>
      <div className="dim" />
      <div className="aurora">
        <span className="a1" />
        <span className="a2" />
      </div>

      <div
        className="modal"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gcc-cashier-title"
        tabIndex={-1}
      >
        <div className="shell">
          <Sidebar
            view={view}
            onNavigate={setView}
            balanceFormatted={balanceFormatted}
            vip={vip}
            withdrawable={withdrawable}
            bonusesCount={bonusesCount}
          />

          <main className="content">
            <div className="chead">
              <div>
                <div className="chead-tt">
                  <h1 id="gcc-cashier-title">{title}</h1>
                  <p>{sub}</p>
                </div>
                <div className="mbal">
                  {__("Balance")}{" "}
                  {balanceFormatted ? <b>{balanceFormatted}</b> : <b className="skel" aria-hidden="true" />}
                </div>
              </div>
              <div
                className="close"
                onClick={handleClose}
                role="button"
                tabIndex={0}
                aria-label={__("Close")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClose();
                  }
                }}
              >
                <CloseIcon />
              </div>
            </div>

            <div className={view === "deposit" ? "view on" : "view"}>
              <DepositView active={view === "deposit"} cashier={cashier} claimedBonusId={claimedBonusId} />
            </div>
            <div className={view === "withdraw" ? "view on" : "view"}>
              <WithdrawView cashier={cashier} />
            </div>
            <div className={view === "bonuses" ? "view on" : "view"}>
              <BonusesView
                cashier={cashier}
                onClaim={(campaignId) => {
                  setClaimedBonusId(campaignId != null ? campaignId : null);
                  setView("deposit");
                }}
              />
            </div>
            <div className={view === "vip" ? "view on" : "view"}>
              <VipView />
            </div>
            <div className={view === "history" ? "view on" : "view"}>
              <HistoryView />
            </div>
            <div className={view === "limits" ? "view on" : "view"}>
              <LimitsView cashier={cashier} />
            </div>
          </main>
        </div>
      </div>
    </div>
    </CashierSharedContext.Provider>
  );
}
